import { useState, FormEvent } from 'react';

const OfferteForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    projectType: '',
    oppervlakte: '',
    budget: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (formData.message.length > 500) return;
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formElement = e.currentTarget;
      const formDataToSend = new FormData(formElement);

      const response = await fetch('https://readdy.ai/api/form/d6msbgiqoe30lj0v8f80', {
        method: 'POST',
        body: new URLSearchParams(formDataToSend as any),
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          address: '',
          projectType: '',
          oppervlakte: '',
          budget: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg p-8 md:p-10">
      <h2 className="text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
        Vraag uw gratis offerte aan
      </h2>
      <p className="text-gray-500 text-sm mb-8">
        Vul het formulier in en wij nemen binnen 24 uur contact met u op.
      </p>

      <form id="offerte-form" onSubmit={handleSubmit} data-readdy-form className="space-y-5">
        {/* Naam & Email */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-800 mb-1.5">
              Naam <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none transition-all text-sm bg-gray-50"
              placeholder="Uw volledige naam"
            />
          </div>
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-800 mb-1.5">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              required
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none transition-all text-sm bg-gray-50"
              placeholder="uw.email@voorbeeld.be"
            />
          </div>
        </div>

        {/* Telefoon & Adres */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-gray-800 mb-1.5">
              Telefoon <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              required
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none transition-all text-sm bg-gray-50"
              placeholder="+32 4 12 34 56 78"
            />
          </div>
          <div>
            <label htmlFor="address" className="block text-sm font-medium text-gray-800 mb-1.5">
              Adres van het project
            </label>
            <input
              type="text"
              id="address"
              name="address"
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none transition-all text-sm bg-gray-50"
              placeholder="Straat, gemeente, postcode"
            />
          </div>
        </div>

        {/* Type Project */}
        <div>
          <label htmlFor="projectType" className="block text-sm font-medium text-gray-800 mb-1.5">
            Type renovatie <span className="text-red-500">*</span>
          </label>
          <select
            id="projectType"
            name="projectType"
            required
            value={formData.projectType}
            onChange={(e) => setFormData({ ...formData, projectType: e.target.value })}
            className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none transition-all text-sm bg-gray-50 cursor-pointer"
          >
            <option value="">Selecteer een type</option>
            <option value="Appartement Renovatie">Appartement Renovatie</option>
            <option value="Badkamer Verbouwing">Badkamer Verbouwing</option>
            <option value="Keuken Renovatie">Keuken Renovatie</option>
            <option value="Buitengevel">Buitengevel</option>
            <option value="Slaapkamer">Slaapkamer</option>
            <option value="Complete Renovatie">Complete Renovatie</option>
            <option value="Anders">Anders</option>
          </select>
        </div>

        {/* Oppervlakte & Budget */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <label htmlFor="oppervlakte" className="block text-sm font-medium text-gray-800 mb-1.5">
              Oppervlakte (m²)
            </label>
            <input
              type="text"
              id="oppervlakte"
              name="oppervlakte"
              value={formData.oppervlakte}
              onChange={(e) => setFormData({ ...formData, oppervlakte: e.target.value })}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none transition-all text-sm bg-gray-50"
              placeholder="bijv. 80 m²"
            />
          </div>
          <div>
            <label htmlFor="budget" className="block text-sm font-medium text-gray-800 mb-1.5">
              Geschat budget
            </label>
            <select
              id="budget"
              name="budget"
              value={formData.budget}
              onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none transition-all text-sm bg-gray-50 cursor-pointer"
            >
              <option value="">Selecteer een budget</option>
              <option value="Minder dan €5.000">Minder dan €5.000</option>
              <option value="€5.000 – €15.000">€5.000 – €15.000</option>
              <option value="€15.000 – €30.000">€15.000 – €30.000</option>
              <option value="€30.000 – €60.000">€30.000 – €60.000</option>
              <option value="Meer dan €60.000">Meer dan €60.000</option>
              <option value="Nog niet bepaald">Nog niet bepaald</option>
            </select>
          </div>
        </div>

        {/* Bericht */}
        <div>
          <label htmlFor="message" className="block text-sm font-medium text-gray-800 mb-1.5">
            Beschrijving van het project <span className="text-red-500">*</span>
          </label>
          <textarea
            id="message"
            name="message"
            required
            rows={5}
            maxLength={500}
            value={formData.message}
            onChange={(e) => setFormData({ ...formData, message: e.target.value })}
            className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none transition-all resize-none text-sm bg-gray-50"
            placeholder="Beschrijf uw renovatieproject zo gedetailleerd mogelijk..."
          />
          <p className={`text-xs mt-1 ${formData.message.length >= 500 ? 'text-red-500' : 'text-gray-400'}`}>
            {formData.message.length}/500 karakters
          </p>
        </div>

        {/* Status messages */}
        {submitStatus === 'success' && (
          <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-sm flex items-center gap-2">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-checkbox-circle-line text-green-600 text-lg"></i>
            </div>
            <span>Bedankt! Uw offerteaanvraag is ontvangen. We nemen binnen 24 uur contact met u op.</span>
          </div>
        )}

        {submitStatus === 'error' && (
          <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm flex items-center gap-2">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-error-warning-line text-red-600 text-lg"></i>
            </div>
            <span>Er is iets misgegaan. Probeer het later opnieuw of bel ons direct.</span>
          </div>
        )}

        <button
          type="submit"
          disabled={isSubmitting || formData.message.length > 500}
          className="w-full bg-gray-900 text-white px-8 py-4 rounded-lg text-base font-semibold hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
        >
          {isSubmitting ? 'Verzenden...' : 'Vraag Gratis Offerte Aan'}
        </button>

        <div className="relative flex items-center gap-3 my-1">
          <div className="flex-1 h-px bg-gray-200"></div>
          <span className="text-xs text-gray-400 font-medium">of</span>
          <div className="flex-1 h-px bg-gray-200"></div>
        </div>

        <a
          href="https://wa.me/3210231886?text=Hallo%2C%20ik%20wil%20graag%20een%20offerte%20aanvragen."
          target="_blank"
          rel="nofollow noopener noreferrer"
          className="w-full flex items-center justify-center gap-3 bg-[#25D366] text-white px-8 py-4 rounded-lg text-base font-medium hover:bg-[#1ebe5d] transition-colors whitespace-nowrap cursor-pointer"
        >
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-whatsapp-line text-xl"></i>
          </div>
          Offerte via WhatsApp
        </a>
      </form>
    </div>
  );
};

export default OfferteForm;
